import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  getDevisById,
  getDevisSortie,
  getDevisAchats,
  getFournisseurs,
  createAchat,
  updateAchat,
  deleteAchat,
  getLots
} from '../services/api';
import { 
  ArrowLeft, 
  Plus, 
  Trash2,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  X,
  Edit
} from 'lucide-react';
import { toast } from 'sonner';

const SortiePage = () => {
  const { devisId } = useParams();
  const [devis, setDevis] = useState(null);
  const [sortie, setSortie] = useState(null);
  const [achats, setAchats] = useState([]);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAchat, setEditingAchat] = useState(null);
  const [newAchat, setNewAchat] = useState({
    lot_id: '',
    fournisseur_nom: '',
    fournisseur_id: '',
    description: '',
    montant: '',
    statut: 'devis'
  });

  useEffect(() => {
    loadData();
  }, [devisId]);

  const loadData = async () => {
    try {
      const [devisRes, sortieRes, achatsRes, fournisseursRes, lotsRes] = await Promise.all([
        getDevisById(devisId),
        getDevisSortie(devisId),
        getDevisAchats(devisId),
        getFournisseurs(),
        getLots()
      ]);
      setDevis(devisRes.data);
      setSortie(sortieRes.data);
      setAchats(achatsRes.data);
      setFournisseurs(fournisseursRes.data);
      setLots(lotsRes.data);
    } catch (e) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAchat = async (e) => {
    e.preventDefault();
    try {
      await createAchat({
        devis_id: devisId,
        lot_id: newAchat.lot_id,
        fournisseur_id: newAchat.fournisseur_id || null,
        fournisseur_nom: newAchat.fournisseur_nom,
        description: newAchat.description,
        montant: parseFloat(newAchat.montant) || 0,
        statut: newAchat.statut
      });
      toast.success('Achat ajouté');
      setShowModal(false);
      setNewAchat({ lot_id: '', fournisseur_nom: '', fournisseur_id: '', description: '', montant: '', statut: 'devis' });
      loadData();
    } catch (e) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleUpdateAchat = async () => {
    if (!editingAchat) return;
    try {
      await updateAchat(editingAchat.id, {
        devis_id: devisId,
        lot_id: editingAchat.lot_id,
        fournisseur_id: editingAchat.fournisseur_id || null,
        fournisseur_nom: editingAchat.fournisseur_nom,
        description: editingAchat.description,
        montant: parseFloat(editingAchat.montant) || 0,
        statut: editingAchat.statut
      });
      toast.success('Achat mis à jour');
      setEditingAchat(null);
      loadData();
    } catch (e) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteAchat = async (id) => {
    if (!window.confirm('Supprimer cet achat ?')) return;
    try {
      await deleteAchat(id);
      toast.success('Achat supprimé');
      loadData();
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleFournisseurSelect = (fournisseurId, isNew = false) => {
    if (isNew) {
      if (fournisseurId) {
        const f = fournisseurs.find(f => f.id === fournisseurId);
        setNewAchat({...newAchat, fournisseur_id: fournisseurId, fournisseur_nom: f?.nom || ''});
      } else {
        setNewAchat({...newAchat, fournisseur_id: '', fournisseur_nom: ''});
      }
    } else {
      if (fournisseurId) {
        const f = fournisseurs.find(f => f.id === fournisseurId);
        setEditingAchat({...editingAchat, fournisseur_id: fournisseurId, fournisseur_nom: f?.nom || ''});
      } else {
        setEditingAchat({...editingAchat, fournisseur_id: ''});
      }
    }
  };

  const formatMoney = (value) => {
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(value);
  };

  const getMargeColor = (marge_pct) => {
    if (marge_pct === null || marge_pct === undefined) return 'bg-slate-100 text-slate-600';
    if (marge_pct < 15) return 'bg-red-100 text-red-700';
    if (marge_pct < 20) return 'bg-orange-100 text-orange-700';
    return 'bg-green-100 text-green-700';
  };

  const getRatioColor = (ratio) => {
    if (ratio === null || ratio === undefined) return 'bg-slate-100 text-slate-600';
    if (ratio < 1.25) return 'bg-red-100 text-red-700';
    if (ratio < 1.33) return 'bg-orange-100 text-orange-700';
    return 'bg-green-100 text-green-700';
  };

  const getStatutBadge = (statut) => {
    switch (statut) {
      case 'facture_payee':
        return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded">Payée</span>;
      case 'facture_validee':
        return <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded">Validée</span>;
      default:
        return <span className="px-2 py-1 text-xs font-medium bg-slate-100 text-slate-600 rounded">Devis</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const totalMargePct = sortie.total_vendu > 0 
    ? ((sortie.total_marge / sortie.total_vendu) * 100) 
    : null;

  return (
    <div data-testid="sortie-page">
      <div className="mb-6 flex items-center justify-between">
        <Link 
          to={`/devis/${devisId}`}
          className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Retour au devis
        </Link>
        <button
          onClick={() => setShowModal(true)}
          data-testid="add-achat-btn"
          className="flex items-center px-4 py-2 bg-slate-900 text-white rounded-md font-medium hover:bg-slate-800 transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" />
          Ajouter un achat
        </button>
      </div>

      {/* Header */}
      <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm mb-6">
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Suivi des coûts et marges</h1>
        <p className="text-slate-500">{sortie.objet} - {sortie.client_nom}</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm" data-testid="total-vendu">
          <p className="text-xs font-medium text-slate-500 mb-1">TOTAL VENDU</p>
          <p className="text-xl font-bold text-slate-900 font-mono">{formatMoney(sortie.total_vendu)}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm" data-testid="total-cout">
          <p className="text-xs font-medium text-slate-500 mb-1">COÛT FOURNISSEURS</p>
          <p className="text-xl font-bold text-slate-900 font-mono">{formatMoney(sortie.total_cout)}</p>
        </div>
        <div className={`rounded-lg border p-4 shadow-sm ${getMargeColor(totalMargePct)}`} data-testid="total-marge">
          <p className="text-xs font-medium opacity-70 mb-1">MARGE TOTALE</p>
          <div className="flex items-center">
            {sortie.total_marge >= 0 ? (
              <TrendingUp className="w-5 h-5 mr-2" />
            ) : (
              <TrendingDown className="w-5 h-5 mr-2" />
            )}
            <p className="text-xl font-bold font-mono">{formatMoney(sortie.total_marge)}</p>
          </div>
          {totalMargePct !== null && (
            <p className="text-sm mt-1">{totalMargePct.toFixed(1)}%</p>
          )}
        </div>
        <div className={`rounded-lg border p-4 shadow-sm ${getRatioColor(sortie.ratio_global)}`} data-testid="ratio-global">
          <p className="text-xs font-medium opacity-70 mb-1">RATIO GLOBAL</p>
          <p className="text-xl font-bold font-mono">
            {sortie.ratio_global ? sortie.ratio_global.toFixed(2) : '-'}
          </p>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-slate-50 rounded-lg p-4 mb-6 flex flex-wrap gap-6 text-sm">
        <div className="flex items-center gap-2">
          <span className="font-medium text-slate-700">Marge:</span>
          <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">&lt; 20%</span>
          <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs">20-25%</span>
          <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">&gt; 25%</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-medium text-slate-700">Ratio:</span>
          <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">&lt; 1.25</span>
          <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs">1.25-1.33</span>
          <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">&gt; 1.33</span>
        </div>
      </div>

      {/* Marges by lot */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm mb-6 overflow-hidden">
        <div className="bg-slate-900 text-white px-6 py-4">
          <h2 className="text-lg font-semibold">MARGE PAR LOT</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Lot</th>
                <th className="px-6 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Vendu</th>
                <th className="px-6 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Coût</th>
                <th className="px-6 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Marge</th>
                <th className="px-6 py-3 text-center text-xs font-bold uppercase tracking-wider text-slate-500">%</th>
                <th className="px-6 py-3 text-center text-xs font-bold uppercase tracking-wider text-slate-500">Ratio</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sortie.marges.filter(m => m.vendu > 0 || m.cout_fournisseurs > 0).map((m) => (
                <tr key={m.lot_id} className="hover:bg-slate-50/50" data-testid={`marge-lot-${m.lot_id}`}>
                  <td className="px-6 py-3 text-sm text-slate-700">{m.lot_nom}</td>
                  <td className="px-6 py-3 text-sm text-slate-900 text-right font-mono">{formatMoney(m.vendu)}</td>
                  <td className="px-6 py-3 text-sm text-slate-900 text-right font-mono">{formatMoney(m.cout_fournisseurs)}</td>
                  <td className="px-6 py-3 text-sm text-right font-mono font-medium">
                    <span className={m.marge >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {formatMoney(m.marge)}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-center">
                    {m.marge_pourcentage !== null && (
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getMargeColor(m.marge_pourcentage)}`}>
                        {m.marge_pourcentage.toFixed(1)}%
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-center">
                    {m.ratio !== null && (
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getRatioColor(m.ratio)}`}>
                        {m.ratio.toFixed(2)}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Achats list */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">DÉTAIL DES ACHATS ({achats.length})</h2>
        </div>
        
        {achats.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Aucun achat enregistré</p>
            <button
              onClick={() => setShowModal(true)}
              className="inline-flex items-center mt-4 text-orange-500 hover:text-orange-600"
            >
              <Plus className="w-4 h-4 mr-1" /> Ajouter un achat
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Lot</th>
                  <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Fournisseur</th>
                  <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Description</th>
                  <th className="px-6 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Montant</th>
                  <th className="px-6 py-3 text-center text-xs font-bold uppercase tracking-wider text-slate-500">Statut</th>
                  <th className="px-6 py-3 text-center text-xs font-bold uppercase tracking-wider text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {achats.map((a) => (
                  <tr key={a.id} className="hover:bg-slate-50/50" data-testid={`achat-${a.id}`}>
                    <td className="px-6 py-3 text-sm text-slate-700">{a.lot_nom}</td>
                    <td className="px-6 py-3 text-sm text-slate-900 font-medium">{a.fournisseur_nom}</td>
                    <td className="px-6 py-3 text-sm text-slate-600">{a.description}</td>
                    <td className="px-6 py-3 text-sm text-slate-900 text-right font-mono font-medium">{formatMoney(a.montant)}</td>
                    <td className="px-6 py-3 text-center">{getStatutBadge(a.statut)}</td>
                    <td className="px-6 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => setEditingAchat({...a})}
                          className="p-1 text-slate-600 hover:bg-slate-100 rounded"
                          data-testid={`edit-achat-${a.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteAchat(a.id)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                          data-testid={`delete-achat-${a.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showModal || editingAchat) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4" data-testid="achat-modal">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">
                {editingAchat ? 'Modifier l\'achat' : 'Nouvel achat'}
              </h2>
              <button 
                onClick={() => { setShowModal(false); setEditingAchat(null); }}
                className="p-1 hover:bg-slate-100 rounded text-slate-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={editingAchat ? (e) => { e.preventDefault(); handleUpdateAchat(); } : handleCreateAchat} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Lot *</label>
                <select
                  value={editingAchat ? editingAchat.lot_id : newAchat.lot_id}
                  onChange={(e) => editingAchat 
                    ? setEditingAchat({...editingAchat, lot_id: e.target.value})
                    : setNewAchat({...newAchat, lot_id: e.target.value})
                  }
                  className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
                  required
                  data-testid="achat-lot-select"
                >
                  <option value="">Sélectionner un lot</option>
                  {lots.map(l => (
                    <option key={l.id} value={l.id}>{l.nom}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Fournisseur</label>
                <select
                  value={editingAchat ? editingAchat.fournisseur_id : newAchat.fournisseur_id}
                  onChange={(e) => handleFournisseurSelect(e.target.value, !editingAchat)}
                  className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm mb-2"
                  data-testid="achat-fournisseur-select"
                >
                  <option value="">Saisie libre</option>
                  {fournisseurs.map(f => (
                    <option key={f.id} value={f.id}>{f.nom}</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Nom du fournisseur"
                  value={editingAchat ? editingAchat.fournisseur_nom : newAchat.fournisseur_nom}
                  onChange={(e) => editingAchat 
                    ? setEditingAchat({...editingAchat, fournisseur_nom: e.target.value})
                    : setNewAchat({...newAchat, fournisseur_nom: e.target.value})
                  }
                  className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
                  required
                  data-testid="achat-fournisseur-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <input
                  type="text"
                  value={editingAchat ? editingAchat.description : newAchat.description}
                  onChange={(e) => editingAchat 
                    ? setEditingAchat({...editingAchat, description: e.target.value})
                    : setNewAchat({...newAchat, description: e.target.value})
                  }
                  className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
                  data-testid="achat-description-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Montant €</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingAchat ? editingAchat.montant : newAchat.montant}
                    onChange={(e) => editingAchat 
                      ? setEditingAchat({...editingAchat, montant: e.target.value})
                      : setNewAchat({...newAchat, montant: e.target.value})
                    }
                    className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
                    required
                    data-testid="achat-montant-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Statut</label>
                  <select
                    value={editingAchat ? editingAchat.statut : newAchat.statut}
                    onChange={(e) => editingAchat 
                      ? setEditingAchat({...editingAchat, statut: e.target.value})
                      : setNewAchat({...newAchat, statut: e.target.value})
                    }
                    className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
                    data-testid="achat-statut-select"
                  >
                    <option value="devis">Devis / Estimatif</option>
                    <option value="facture_validee">Facture validée</option>
                    <option value="facture_payee">Facture payée</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingAchat(null); }}
                  className="flex-1 h-10 border border-slate-300 text-slate-700 rounded-md font-medium hover:bg-slate-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  data-testid="save-achat-btn"
                  className="flex-1 h-10 bg-slate-900 text-white rounded-md font-medium hover:bg-slate-800"
                >
                  {editingAchat ? 'Mettre à jour' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SortiePage;
